/**
 * useWizard — a step TREE with branches, nested sub-flows and a value handed back.
 *
 * Lookout has three bespoke multi-step flows (the activation sheet, the RudderStack add-account
 * drawer, the warehouse add-account wizard, whose own header asked for this once a second flow
 * appeared). rudder-webapp's `MultiStepModal` supplies the model — a tree whose branches are a node's
 * children — but its sub-flows cannot cancel to their parent and its last step always closes the
 * whole flow, which is exactly what a "create an account without leaving the sync" step needs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { footerState, type WizardFooterState } from '@/_shared/lib/wizard/footer';
import { exitKey, nextKey, prevKey } from '@/_shared/lib/wizard/navigate';
import { indexTree, pathTo } from '@/_shared/lib/wizard/tree';
import type { WizardStep, WizardTree } from '@/_shared/lib/wizard/types';

/** Returns false to veto a close — the port of the activation sheet's own interceptor. */
export type CloseInterceptor = () => boolean;

export interface UseWizardOptions<K extends string, R> {
  tree: WizardTree<K>;
  initialStep: K;
  /**
   * Label for the primary button on the last step. Defaults to "Done".
   *
   * ONE label for the whole wizard, deliberately — and that is why it cannot be the answer for a
   * sub-flow's terminal step. `footerState` calls any step with no forward move "the last one", so a
   * tree with a sub-flow has two of them: the wizard's own ("Enable sync") and the sub-flow's
   * ("Connect", "Save"). A second option here would only push the problem one level down, since a
   * sub-flow's label also varies by what the user picked inside it.
   *
   * The settled rule (Task 7, `AudienceActivationSheet`): a step body that owns its primary REPORTS
   * it to the shell — label, disabled, loading and a stable `onClick` — and the shell renders that in
   * place of `footer.primaryLabel` while it is on that step. `doneLabel` stays the wizard-level
   * default for every step that has nothing to say. See `AddAccountChrome`.
   */
  doneLabel?: string;
  /** Called with a sub-flow's result and the sub-flow root it came from, before returning there. */
  onSubFlowComplete?: (result: R, from: K) => void;
}

export interface Wizard<K extends string, R> {
  stepKey: K;
  step: WizardStep<K> | undefined;
  path: K[];
  footer: WizardFooterState;
  goNext: (requested?: K) => void;
  goBack: () => void;
  goTo: (key: K) => void;
  /**
   * Exits the nearest enclosing sub-flow, returning to its `cancelTo` step. Returns false, and moves
   * nowhere, when the current step isn't inside a sub-flow — the caller (not this hook) decides
   * whether that means closing the wizard instead.
   */
  cancelSubFlow: () => boolean;
  /**
   * Hands `result` to `onSubFlowComplete` and exits the nearest enclosing sub-flow, same as
   * {@link Wizard.cancelSubFlow}. Returns false, and moves nowhere, when the current step isn't
   * inside a sub-flow.
   */
  completeSubFlow: (result: R) => boolean;
  reset: () => void;
  /**
   * Adds a close interceptor and returns its disposer. **Calling the disposer is mandatory.** This is
   * a Set, not the single re-registered slot `AudienceActivationSheet` uses, so an effect that
   * registers without cleaning up leaves one stale closure per render behind — and since
   * {@link Wizard.requestClose} stops at the first veto, a single stale closure that captured
   * `isSaving === true` vetoes closing forever, with no way to enumerate or clear the Set from
   * outside. Return the disposer from the effect; do not port the sheet's
   * `registerCloseInterceptor(null)` habit, which clears a slot this registry does not have.
   */
  registerCloseInterceptor: (fn: CloseInterceptor) => () => void;
  /**
   * False when any interceptor vetoes, and then the caller must not close. Returns on the FIRST veto,
   * so the remaining interceptors are never consulted: none of them may rely on being called for a
   * side effect (focusing the offending field, opening a confirm dialog). Order within the Set is
   * registration order, which is render order — not something a consumer should lean on.
   */
  requestClose: () => boolean;
}

export function useWizard<K extends string, R = void>({
  tree,
  initialStep,
  doneLabel,
  onSubFlowComplete,
}: UseWizardOptions<K, R>): Wizard<K, R> {
  // The tree is authored as a module constant; indexing it once keeps every transition O(1).
  const index = useMemo(() => {
    const built = indexTree(tree);
    /**
     * Throw rather than fall back to the first root. A silent fallback is how a step with no body, a
     * live primary and no Back reaches the screen in the first place, and `reset()` would keep
     * returning to it for the rest of the session. `initialStep` is authored next to the tree, so
     * this fails at mount in development, not in front of a user.
     */
    if (!built.byKey.has(initialStep)) {
      throw new Error(`Wizard initialStep "${initialStep}" is not in the tree`);
    }
    return built;
  }, [tree, initialStep]);
  const [stepKey, setStepKey] = useState<K>(initialStep);
  /**
   * Mirrors `stepKey`, written synchronously by every mutator via `commit`. `setState` doesn't apply
   * until the next render, so a caller that chains two mutators in one handler (`goNext(...)` then
   * `completeSubFlow(...)`) needs somewhere to read the step the first call just produced — an
   * effect-synced mirror is a render too late for that and would read the step from *before* the
   * chain started.
   */
  const stepKeyRef = useRef(stepKey);
  /** Mirrors the latest `onSubFlowComplete`, the same "ref synced in an effect" idiom `useRunOnceWhen`
   * uses for its callback — unlike `stepKeyRef` this one only needs the latest render's value, never a
   * value from earlier in the same handler, so syncing after commit (rather than inside `commit`) is
   * fine, and it keeps `completeSubFlow`'s identity stable even when the caller passes an inline
   * lambda. */
  const onSubFlowCompleteRef = useRef(onSubFlowComplete);
  useEffect(() => {
    onSubFlowCompleteRef.current = onSubFlowComplete;
  });
  const interceptors = useRef<Set<CloseInterceptor>>(new Set());

  /** The one place `stepKey` changes, so `stepKeyRef` can never fall out of sync with it. */
  const commit = useCallback((next: K) => {
    stepKeyRef.current = next;
    setStepKey(next);
  }, []);

  const goTo = useCallback(
    (key: K) => {
      if (index.byKey.has(key)) commit(key);
    },
    [index, commit],
  );

  const goNext = useCallback(
    (requested?: K) => {
      const current = stepKeyRef.current;
      commit(nextKey(index, current, requested) ?? current);
    },
    [index, commit],
  );

  const goBack = useCallback(() => {
    const current = stepKeyRef.current;
    commit(prevKey(index, current) ?? current);
  }, [index, commit]);

  const cancelSubFlow = useCallback((): boolean => {
    const next = exitKey(index, stepKeyRef.current);
    if (next === null) return false;
    commit(next);
    return true;
  }, [index, commit]);

  const completeSubFlow = useCallback(
    (result: R): boolean => {
      const from = stepKeyRef.current;
      onSubFlowCompleteRef.current?.(result, from);
      const next = exitKey(index, from);
      if (next === null) return false;
      commit(next);
      return true;
    },
    [index, commit],
  );

  const reset = useCallback(() => commit(initialStep), [initialStep, commit]);

  const registerCloseInterceptor = useCallback((fn: CloseInterceptor) => {
    interceptors.current.add(fn);
    return () => {
      interceptors.current.delete(fn);
    };
  }, []);

  /** False when any interceptor vetoes — the caller must not close. First veto wins and short-circuits. */
  const requestClose = useCallback(() => {
    for (const fn of interceptors.current) if (!fn()) return false;
    return true;
  }, []);

  const footer = useMemo(() => footerState(index, stepKey, { doneLabel }), [index, stepKey, doneLabel]);
  // Memoized like `footer` beside it: a parent-chain walk and a fresh array on every render, and the
  // activation sheet re-renders on every keystroke in its configure step.
  const path = useMemo(() => pathTo(index, stepKey), [index, stepKey]);

  return {
    stepKey,
    step: index.byKey.get(stepKey),
    path,
    footer,
    goNext,
    goBack,
    goTo,
    cancelSubFlow,
    completeSubFlow,
    reset,
    registerCloseInterceptor,
    requestClose,
  };
}
